import { ComponentProps, useEffect, useState } from 'react';

import { classNames, twMerge } from '../../../utils/classnames';
import { ApplicationBanner } from '../ApplicationBanner';

/**
 * DOM id for the fixed container that wraps the announcement banner and the header.
 * Exported so other components can measure its current height when positioning
 * sticky UI directly below it.
 */
export const FIXED_HEADER_CONTAINER_ID = 'matcha-fixed-header-container';

/**
 * This component is the base for all headers.
 * It contains the styling for the bar as well as the logic to trigger the blur effect
 */
export function HeaderBase({ children, className, ...props }: ComponentProps<'header'>) {
  const [shouldBlurBackground, setShouldBlurBackground] = useState(false);
  const [sentinelElement, setSentinelElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sentinelElement) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => setShouldBlurBackground(!entries[0].isIntersecting),
      { rootMargin: '0px', threshold: 0 },
    );

    observer.observe(sentinelElement);

    return () => observer.disconnect();
  }, [sentinelElement]);

  return (
    <>
      <div ref={setSentinelElement} className="absolute top-[10px] h-px" aria-hidden />
      <div id={FIXED_HEADER_CONTAINER_ID} className="fixed top-0 z-[100] flex flex-col w-full">
        <ApplicationBanner />
        <header
          className={twMerge(
            classNames(
              'group',
              '[&[data-should-blur="true"]]:backdrop-blur-[15px]',
              '[&[data-should-blur="true"]]:border-b',
              '[&[data-should-blur="true"]]:border-token-alphalight-300',
              '[&[data-should-blur="false"]]:backdrop-blur-none',
              'flex items-center justify-between',
              'w-full',
              'py-1.5',
              'pl-4',
              'z-[100]',
              'h-[62px]',
              'pr-[calc(var(--spacing)*3+var(--removed-body-scroll-bar-size,0px))]',
              'transition-[background,backdrop-filter] duration-200 ease-in-out',
              'tablet:pr-[calc(var(--spacing)*6+var(--removed-body-scroll-bar-size,0px))]',
              className,
            ),
          )}
          data-should-blur={shouldBlurBackground}
          {...props}
        >
          {children}
        </header>
      </div>
    </>
  );
}
